// Effect 7 — Particle burst. A custom canvas emitter (no dependency) so we control the feel and so
// the burst is deterministic under a seed (velocities come from the seeded rng). Fires from the
// centre of the utterance row on a valid word.
import { feel } from "../feel";
import { rand, range } from "../../core/rng";
import { registerEffect } from "../registry";
import { fxCtx } from "../fxlayer";
import { isFrozen } from "../anim";
import { intensity, timed } from "../../tuner/scale";
import { reduceMotion } from "../a11y";

interface P {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  born: number;
  hue: number;
}

const particles: P[] = [];
let running = false;

export function registerParticles(): void {
  registerEffect(
    { name: "particles", label: "7 · Particles" },
    "word:valid",
    ({ tiles }) => {
      if (reduceMotion()) return; // flying particles are motion — skip
      const row = (tiles[0]?.parentElement as HTMLElement) ?? document.getElementById("stage");
      if (!row) return;
      const r = row.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const now = performance.now();
      const I = intensity(feel.particles); // intensity scales both burst size and speed
      const count = Math.round(feel.particles.count * I);
      for (let i = 0; i < count; i++) {
        const ang = range(0, Math.PI * 2);
        const spd = feel.particles.speed * I * (0.4 + rand() * feel.particles.spread);
        particles.push({
          x: cx,
          y: cy,
          vx: Math.cos(ang) * spd,
          vy: Math.sin(ang) * spd,
          life: timed(feel.particles.lifeMs),
          born: now,
          hue: 40 + rand() * 20, // hostile paperwork amber/yellow
        });
      }
      if (!running) loop();
    },
  );
}

function loop(): void {
  running = true;
  let last = performance.now();
  const ctx = fxCtx();
  function frame(now: number) {
    const dt = isFrozen() ? 0 : (now - last) / 1000;
    last = now;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      const age = now - p.born;
      if (age >= p.life) {
        particles.splice(i, 1);
        continue;
      }
      p.vy += feel.particles.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const alpha = 1 - age / p.life;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = `hsl(${p.hue} 90% 60%)`;
      ctx.fillRect(p.x, p.y, feel.particles.size, feel.particles.size);
    }
    ctx.globalAlpha = 1;
    if (particles.length) requestAnimationFrame(frame);
    else {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      running = false;
    }
  }
  requestAnimationFrame(frame);
}
